/**
 * LadiesHubChipBar - Premium Sticky Category Chip Bar
 * V2: Upgraded to match Ladies Hub Luxury gradient theme (Rose → Coral → Gold)
 * Features: Spring animations, floating design, scroll arrows, gradient selected state
 */

import React, { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { LadiesHubMotion } from '../hubs/ladies/LadiesHubPremiumConfig';
import { getLadiesIcon } from '../hubs/ladies/ladiesIconMap';
import { hapticSelection } from '../utils/haptics';

const motionConfig = LadiesHubMotion;

// Luxury color palette matching hero
const luxColors = {
  rose: '#d63384',
  coral: '#ff7a7a',
  gold: '#ffb347',
  bg: '#fbf7f1',
  ink: '#1f1f1f',
  muted: '#6b6b6b',
};

export default function LadiesHubChipBar({
  tabs,
  activeTab,
  onTabSelect,
  isRTL = false,
  lang = 'en',
  categoryCounts = {},
}) {
  const scrollRef = useRef(null);

  // iter330 — One-time auto-nudge so the user sees the chip bar is
  // horizontally scrollable. Persisted via sessionStorage (so it
  // doesn't repeat on every navigation) and skipped entirely when
  // prefers-reduced-motion is enabled.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const NUDGE_KEY = 'wassla-nudge-ladies-chipbar';

    const mq = typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;
    if (mq && mq.matches) return undefined;

    try {
      if (sessionStorage.getItem(NUDGE_KEY)) return undefined;
    } catch { /* sessionStorage may be blocked */ }

    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 4) return undefined;

    const sign = isRTL ? -1 : 1;
    const t1 = setTimeout(() => {
      try { el.scrollBy({ left: sign * 56, behavior: 'smooth' }); } catch { /* noop */ }
    }, 700);
    const t2 = setTimeout(() => {
      try { el.scrollBy({ left: -sign * 56, behavior: 'smooth' }); } catch { /* noop */ }
      try { sessionStorage.setItem(NUDGE_KEY, '1'); } catch { /* noop */ }
    }, 1350);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [isRTL]);

  const scrollChips = (direction) => {
    if (!scrollRef.current) return;
    const scrollAmount = 200;
    const actualScroll = isRTL ? -direction * scrollAmount : direction * scrollAmount;
    scrollRef.current.scrollBy({ left: actualScroll, behavior: 'smooth' });
  };

  const handleChipClick = async (tabId) => {
    await hapticSelection();
    onTabSelect(tabId);
  };

  const getLabel = (tab) => {
    const base = lang === 'ar' ? tab.label_ar : tab.label_en;
    if (tab.id === 'all') return base;
    const count = categoryCounts[tab.id];
    return count ? `${base} (${count})` : base;
  };

  return (
    <motion.div
      className="ladies-premium-chipbar-v2"
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={motionConfig.springSoft}
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '10px 8px',
        background: `linear-gradient(180deg, ${luxColors.bg}f8 0%, ${luxColors.bg}f0 100%)`,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: `1px solid rgba(214, 51, 132, 0.12)`,
        boxShadow: '0 4px 20px rgba(214, 51, 132, 0.08)',
      }}
      data-testid="ladies-premium-chipbar"
    >
      {/* Prev Arrow */}
      <motion.button
        whileTap={{ scale: 0.9 }}
        onClick={() => scrollChips(-1)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 34,
          height: 34,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.8)',
          border: `1.5px solid ${luxColors.coral}`,
          color: luxColors.coral,
          cursor: 'pointer',
          flexShrink: 0,
          transition: 'transform 0.12s ease',
        }}
        aria-label={lang === 'ar' ? 'السابق' : 'Previous'}
        data-testid="chipbar-scroll-prev"
      >
        {isRTL ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
      </motion.button>

      {/* Scrollable Chips */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          display: 'flex',
          gap: 10,
          overflowX: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          padding: '2px 0',
          justifyContent: 'flex-start',
        }}
        className="hide-scrollbar"
      >
        <AnimatePresence mode="popLayout">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <motion.button
                key={tab.id}
                layout
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                whileTap={{ scale: 0.95 }}
                transition={motionConfig.spring}
                onClick={() => handleChipClick(tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '10px 14px',
                  height: 42,
                  borderRadius: 999,
                  background: isActive 
                    ? `linear-gradient(135deg, ${luxColors.coral} 0%, ${luxColors.gold} 100%)`
                    : 'rgba(255, 255, 255, 0.65)',
                  border: isActive 
                    ? 'none' 
                    : `1px solid rgba(214, 51, 132, 0.18)`,
                  color: isActive ? '#FFFFFF' : luxColors.ink,
                  fontWeight: isActive ? 700 : 600,
                  fontSize: 14,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  boxShadow: isActive 
                    ? '0 8px 24px rgba(255, 122, 122, 0.35)' 
                    : '0 4px 12px rgba(31, 31, 31, 0.04)',
                  transition: 'transform 0.12s ease, box-shadow 0.12s ease',
                  WebkitTapHighlightColor: 'transparent',
                }}
                data-testid={`chip-${tab.id}`}
              >
                <span style={{ 
                  fontSize: 14,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 26,
                  height: 26,
                  borderRadius: 999,
                  background: isActive 
                    ? 'rgba(255, 255, 255, 0.22)' 
                    : 'linear-gradient(135deg, rgba(214, 51, 132, 0.10), rgba(255, 180, 71, 0.14))',
                  color: isActive ? '#FFFFFF' : luxColors.rose,
                  flexShrink: 0,
                }}>
                  {/* iter285 — render Lucide vector icon for premium feel.
                      Falls back to the emoji stored on the category record
                      if the id is new and not yet mapped. */}
                  {(() => {
                    const Icon = getLadiesIcon(tab.id);
                    if (Icon) {
                      return <Icon size={15} strokeWidth={2.25} aria-hidden="true" />;
                    }
                    return tab.icon;
                  })()}
                </span>
                <span>{getLabel(tab)}</span>
              </motion.button>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Next Arrow */}
      <motion.button
        whileTap={{ scale: 0.9 }}
        onClick={() => scrollChips(1)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 34,
          height: 34,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.8)',
          border: `1.5px solid ${luxColors.coral}`,
          color: luxColors.coral,
          cursor: 'pointer',
          flexShrink: 0,
          transition: 'transform 0.12s ease',
        }}
        aria-label={lang === 'ar' ? 'التالي' : 'Next'}
        data-testid="chipbar-scroll-next"
      >
        {isRTL ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
      </motion.button>

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .ladies-premium-chipbar-v2 button:active {
          transform: scale(0.98);
        }
        .ladies-premium-chipbar-v2 button:focus-visible {
          outline: 2px solid rgba(255, 140, 0, 0.45);
          outline-offset: 2px;
        }
        html.dark .ladies-premium-chipbar-v2 {
          background: linear-gradient(180deg, rgba(28, 25, 23, 0.98) 0%, rgba(28, 25, 23, 0.95) 100%) !important;
          border-bottom-color: rgba(214, 51, 132, 0.2) !important;
        }
      `}</style>
    </motion.div>
  );
}
